import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';

export type NoticeType = 'error' | 'notification' | 'warning' | 'success';

@Component({
  selector: 'lib-notice',
  imports: [MatIcon],
  template: `
    <mat-icon class="notice-icon" data-testid="icon">{{ icon() }}</mat-icon>
    <div class="notice-content">
      @if (message()) {
        <p class="mat-label-medium" data-testid="message">
          {{ message() }}
        </p>
      }
      <ng-content></ng-content>
    </div>
  `,
  styles: `
    @use '@angular/material' as mat;
    @use 'theme' as theme;
    @use 'breakpoints' as *;

    :host {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      padding: 8px;
      border-left: 4px solid;
      gap: 0.5rem;

      &.error {
        background-color: #{mat.get-theme-color(
            theme.$light-theme,
            error-container
          )};
        border-left-color: #{mat.get-theme-color(theme.$light-theme, error)};

        .notice-icon {
          color: #{mat.get-theme-color(theme.$light-theme, error)};
        }
      }

      &.warning {
        background-color: lighten(#fa9949, 30%);
        border-left-color: #fa9949;

        .notice-icon {
          color: #fa9949;
        }
      }

      &.notification {
        background-color: #e6f5fd;
        border-left-color: #0095ec;
      }

      .notice-icon {
        flex-shrink: 0;
        margin-top: 0.25rem;
      }

      &.success {
        background-color: theme.$success-container;
        border-left-color: #1c5230;
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NoticeComponent {
  private readonly elementRef = inject(ElementRef);

  noticeType = input<NoticeType>('notification');
  message = input<string>();

  icon = computed(() => {
    switch (this.noticeType()) {
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'success':
        return 'check_circle';
      case 'notification':
      default:
        return 'notifications_none';
    }
  });

  constructor() {
    afterRenderEffect({
      write: (cleanup) => {
        const hostElement = this.elementRef.nativeElement;
        hostElement.classList.add(this.noticeType());

        cleanup(() => {
          hostElement.classList.remove(this.noticeType());
        });
      },
    });
  }
}
